import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { Input } from '@/shared/ui/inputs/Input';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Select } from '@/shared/ui/selects/Select';
import { Section } from '@/shared/ui/layout/Section';
import { Button } from '@/shared/ui/buttons/Button';
import { Trash2Icon } from '@/shared/ui/icons';
import { REGION_VALUES } from '@/shared/utils/regions';
import { env } from '@/shared/lib/env';
import { cn } from '@/shared/utils/cn';
import { formatUsd, roundMoney } from '@/shared/utils/currency';
import { useAddresses } from '@/entities/addresses';
import { isCountedInLimit, usePinflLimit } from '@/entities/pinfl';
import { usePackagesNextName } from '@/entities/packages';
import {
  applyRecipient,
  formatFullName,
  recipientMatchesValues,
  restoreScrollAfterApply,
  type RecipientField,
} from '@/shared/lib/recipient';
import { handleUppercaseChange } from '@/shared/lib/forms';
import type { ApiParcelsProductsGetItem } from '@/shared/types/api/parcels-products';
import type { SplitTargetsFormValues } from '@/features/splits/modules/types';

type Props = {
  form: UseFormReturn<SplitTargetsFormValues>;
  index: number;
  onRemove: () => void;
  canRemove: boolean;
  // Parcel products so this target can allocate quantities + show the computed
  // package price preview alongside each row.
  products: ApiParcelsProductsGetItem[];
  // Source parcel id: the limit baseline is fetched with
  // ``exclude_parcel=<id>`` so this parcel's own (being-redistributed) products
  // are never double-counted, for ANY target PINFL (owner or not).
  parcelId: string;
};

// Passport series mirrors admin: 9 chars total (2 uppercase Latin + 7 digits).
const PASSPORT_MAX = 9;

export const FeaturesSplitsTargetFields = ({
  form,
  index,
  onRemove,
  canRemove,
  products,
  parcelId,
}: Props) => {
  const { t } = useTranslation('splits');
  const { register, control, formState, setValue } = form;
  const errors = formState.errors.targets?.[index];

  const { data } = useAddresses();
  const addresses = useMemo(() => data ?? [], [data]);

  const nextName = usePackagesNextName();

  // Reactively read this target's current values so we can derive which saved
  // recipient (if any) the form currently equals. Fully derived: no local state,
  // no clear effect. Immune to remounts, re-renders, react-query refetches, and
  // RHF event-type quirks. After a pick fills the fields, targetValues updates
  // via useWatch, selectedId recomputes to that address's id, and the Select
  // shows it automatically; a user edit diverges a field so nothing matches and
  // the placeholder returns.
  const targetValues = useWatch({ control, name: `targets.${index}` });
  const selectedId =
    addresses.find((a) => recipientMatchesValues(a, targetValues))?.id ?? '';

  // Customs spending limit for the recipient's live PINFL. Called
  // unconditionally with the watched value; the hook self-disables until the
  // PINFL is complete, so the empty-string fallback is harmless.
  const { data: pinflLimit } = usePinflLimit(targetValues?.pinfl ?? '', parcelId);

  // Auto-generated package name (read-only). Weight/unit/price are no longer
  // shown or edited on the client.
  const packageName = targetValues?.package_name ?? '';

  const regionOptions = REGION_VALUES.map((value) => ({ value, label: t(`regions:${value}`) }));
  const passportField = register(`targets.${index}.passport_series`);

  const recipientOptions = addresses.map((a) => ({
    value: a.id,
    label: a.label?.trim() ? a.label : formatFullName(a, t('pickRecipient.noName')),
  }));

  const handlePickRecipient = (id: string): void => {
    if (!id) return;
    const found = addresses.find((a) => a.id === id);
    if (!found) return;
    const opts = { shouldDirty: true, shouldTouch: true } as const;
    const set = (field: RecipientField, value: string): void =>
      form.setValue(`targets.${index}.${field}`, value as never, opts);
    restoreScrollAfterApply(() => applyRecipient(found, set));
  };

  // Auto-generate the package name exactly once per target as soon as the field
  // is empty (a fresh target). No user action — the name is always machine
  // assigned. The ref guard makes the fetch fire-once: it survives the brief
  // window where package_name is still '' before onSuccess lands and ignores
  // the nextName mutation-state identity changes that re-run this effect, so the
  // server counter is never double-incremented and there is no regeneration
  // loop. Existing targets (edit flow) arrive with a name already set and are
  // skipped, preserving their saved value.
  const autoNamedRef = useRef(false);
  useEffect(() => {
    if (autoNamedRef.current || packageName) return;
    autoNamedRef.current = true;
    nextName.mutate(undefined, {
      onSuccess: (res) =>
        setValue(`targets.${index}.package_name`, res.name, {
          shouldDirty: true,
          shouldValidate: true,
        }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageName]);

  return (
    <Section
      title={t('targetTitle', { index: index + 1 })}
      action={
        canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            leftIcon={<Trash2Icon className="h-4 w-4" />}
            onClick={onRemove}
          >
            {t('removeTarget')}
          </Button>
        ) : undefined
      }
    >
      {addresses.length > 0 ? (
        <div className="flex flex-col gap-1">
          <Select
            label={t('pickRecipient.title')}
            options={recipientOptions}
            placeholder={t('pickRecipient.placeholder')}
            value={selectedId}
            onChange={(e) => handlePickRecipient(e.target.value)}
          />
          <p className="text-xs text-ink-muted">{t('pickRecipient.autoFillHint')}</p>
        </div>
      ) : null}
      <Input
        label={t('fields.pinfl')}
        required
        inputMode="numeric"
        maxLength={env.pinflLength}
        placeholder={t('fields.pinflPlaceholder', { length: env.pinflLength })}
        error={errors?.pinfl?.message ? t(errors.pinfl.message, { length: env.pinflLength }) : undefined}
        {...register(`targets.${index}.pinfl`)}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label={t('fields.lastName')}
          required
          error={errors?.last_name?.message ? t(errors.last_name.message) : undefined}
          {...register(`targets.${index}.last_name`)}
        />
        <Input
          label={t('fields.firstName')}
          required
          error={errors?.first_name?.message ? t(errors.first_name.message) : undefined}
          {...register(`targets.${index}.first_name`)}
        />
        <Input
          label={t('fields.middleName')}
          required
          error={errors?.middle_name?.message ? t(errors.middle_name.message) : undefined}
          {...register(`targets.${index}.middle_name`)}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label={t('fields.phone')}
          required
          inputMode="tel"
          placeholder={t('fields.phonePlaceholder')}
          error={errors?.phone_number?.message ? t(errors.phone_number.message) : undefined}
          {...register(`targets.${index}.phone_number`)}
        />
        <Input
          label={t('fields.passport')}
          required
          placeholder={t('fields.passportPlaceholder')}
          autoCapitalize="characters"
          maxLength={PASSPORT_MAX}
          error={errors?.passport_series?.message ? t(errors.passport_series.message) : undefined}
          {...passportField}
          onChange={handleUppercaseChange(passportField.onChange)}
        />
      </div>
      <Input
        label={t('fields.addressName')}
        required
        error={errors?.address_name?.message ? t(errors.address_name.message) : undefined}
        {...register(`targets.${index}.address_name`)}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Controller
          control={control}
          name={`targets.${index}.region`}
          render={({ field }) => (
            <Select
              label={t('fields.region')}
              required
              options={regionOptions}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={errors?.region?.message ? t(errors.region.message) : undefined}
            />
          )}
        />
        <Input
          label={t('fields.city')}
          required
          error={errors?.city?.message ? t(errors.city.message) : undefined}
          {...register(`targets.${index}.city`)}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label={t('fields.street')}
          required
          error={errors?.street?.message ? t(errors.street.message) : undefined}
          {...register(`targets.${index}.street`)}
        />
        <Input
          label={t('fields.building')}
          required
          error={errors?.building?.message ? t(errors.building.message) : undefined}
          {...register(`targets.${index}.building`)}
        />
        <Input
          label={t('fields.apartment')}
          error={errors?.apartment?.message ? t(errors.apartment.message) : undefined}
          {...register(`targets.${index}.apartment`)}
        />
      </div>
      <Textarea
        label={t('fields.additionalInfo')}
        rows={3}
        error={errors?.additional_info?.message ? t(errors.additional_info.message) : undefined}
        {...register(`targets.${index}.additional_info`)}
      />

      {/* Package: auto-generated name only (read-only). Weight/unit/price are
          managed by the warehouse/admin, not entered on the client. */}
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          {t('packageInfo')}
        </p>
        <Input
          label={t('fields.packageName')}
          required
          disabled
          placeholder={t('fields.packageNamePlaceholder')}
          error={errors?.package_name?.message ? t(errors.package_name.message) : undefined}
          {...register(`targets.${index}.package_name`)}
        />
      </div>

      {/* Allocations: per-product quantity assigned to this target --------- */}
      <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          {t('distribution.title')}
        </p>
        {pinflLimit &&
          pinflLimit.limit_applies !== false &&
          (() => {
            // Live customs-limit running total: baseline usage (server-side,
            // already excluding this source parcel) plus the cost of the
            // quantities entered in this section. Mirrors the admin PinflCard
            // math (used = baseline + allocSum, over = used > ceiling).
            const baselineUsed = pinflLimit.used_amount;
            // Products created before the usage window (last period / before
            // an admin reset) do not count on the server, so skip them here.
            const allocSum = (targetValues?.allocations ?? []).reduce((sum, alloc) => {
              if (!isCountedInLimit(pinflLimit, alloc.product_id)) return sum;
              const product = products.find((p) => p.id === alloc.product_id);
              return sum + (Number(alloc.quantity) || 0) * (Number(product?.price_per_item) || 0);
            }, 0);
            const displayedUsed = roundMoney(baselineUsed + allocSum);
            return (
              <p
                className={cn(
                  'text-xs',
                  displayedUsed > pinflLimit.limit ? 'text-danger' : 'text-emerald-700',
                )}
              >
                {t('limit')}: {formatUsd(displayedUsed)} / {formatUsd(pinflLimit.limit)}
              </p>
            );
          })()}
        {products.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('distribution.noProducts')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {products.map((product, pIdx) => (
              <div
                key={product.id}
                className="flex items-center justify-between gap-3"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-ink-soft">
                  {product.full_name}{' '}
                  <span className="text-ink-muted">{t('distribution.available', { count: product.quantity })}</span>
                </span>
                <div className="w-24 shrink-0">
                  <Controller
                    control={control}
                    name={`targets.${index}.allocations.${pIdx}.quantity`}
                    render={({ field }) => (
                      <Input
                        inputMode="numeric"
                        value={field.value ? String(field.value) : ''}
                        placeholder="0"
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^0-9]/gu, '');
                          field.onChange(digits === '' ? 0 : Number(digits));
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
};
